import type { HeadingTagType, SerializedHeadingNode } from "@payloadcms/richtext-lexical/lexical/rich-text";
import { HeadingNode } from "@payloadcms/richtext-lexical/lexical/rich-text";
import type {
  DOMConversionMap,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
} from "@payloadcms/richtext-lexical/lexical";

export type HeadingFontSizeClass = HeadingTagType | null;

export type SerializedCustomHeadingNode = SerializedHeadingNode & {
  type: "heading-styled";
  fontSizeClass?: HeadingTagType;
};

const FONT_SIZE_BY_HEADING: Record<HeadingTagType, string> = {
  h1: "1.75em",
  h2: "1.5625em",
  h3: "1.375em",
  h4: "1.25em",
  h5: "1.125em",
  h6: "1em",
};

const FONT_WEIGHT_BY_HEADING: Record<HeadingTagType, string> = {
  h1: "700",
  h2: "700",
  h3: "600",
  h4: "600",
  h5: "600",
  h6: "600",
};

const applyHeadingStyleOverride = (
  dom: HTMLElement,
  fontSizeClass: HeadingFontSizeClass,
): void => {
  dom.dataset.headingStyleClass = fontSizeClass ?? "";

  if (!fontSizeClass) {
    dom.style.removeProperty("font-size");
    dom.style.removeProperty("font-weight");
    dom.style.removeProperty("line-height");
    return;
  }

  dom.style.fontSize = FONT_SIZE_BY_HEADING[fontSizeClass];
  dom.style.fontWeight = FONT_WEIGHT_BY_HEADING[fontSizeClass];
  dom.style.lineHeight = "1.2";
};

/**
 * A distinct Lexical node type for headings that carry a font-size class
 * override. It extends HeadingNode so that $isHeadingNode() (instanceof check)
 * still returns true, meaning the heading-level toolbar (H1 / H2 / …) keeps
 * working correctly.
 *
 * The type is "heading-styled" — different from "heading" — so there is no
 * conflict with the built-in HeadingNode registration.
 *
 * Backward-compat: plain "heading" nodes continue to render normally; only
 * nodes that the editor explicitly saves as "heading-styled" get the override.
 */
export class CustomHeadingNode extends HeadingNode {
  __fontSizeClass: HeadingFontSizeClass;

  constructor(tag: HeadingTagType, fontSizeClass?: HeadingFontSizeClass, key?: NodeKey) {
    super(tag, key);
    this.__fontSizeClass = fontSizeClass ?? null;
  }

  static getType(): string {
    return "heading-styled";
  }

  static clone(node: CustomHeadingNode): CustomHeadingNode {
    return new CustomHeadingNode(node.__tag, node.__fontSizeClass, node.__key);
  }

  getFontSizeClass(): HeadingFontSizeClass {
    return this.getLatest().__fontSizeClass;
  }

  setFontSizeClass(fontSizeClass: HeadingFontSizeClass): void {
    const writable = this.getWritable();
    writable.__fontSizeClass = fontSizeClass;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    applyHeadingStyleOverride(dom, this.__fontSizeClass);
    return dom;
  }

  updateDOM(
    prevNode: this,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    const shouldReplaceDOM = super.updateDOM(prevNode, dom, config);

    if (
      prevNode.__fontSizeClass !== this.__fontSizeClass ||
      !dom.dataset.headingStyleClass
    ) {
      applyHeadingStyleOverride(dom, this.__fontSizeClass);
    }

    return shouldReplaceDOM;
  }

  exportJSON(): SerializedCustomHeadingNode {
    const base = super.exportJSON() as SerializedElementNode & { tag: HeadingTagType };
    return {
      ...base,
      type: "heading-styled",
      ...(this.__fontSizeClass ? { fontSizeClass: this.__fontSizeClass } : {}),
    };
  }

  static importJSON(serialized: SerializedCustomHeadingNode): CustomHeadingNode {
    const node = new CustomHeadingNode(
      serialized.tag,
      serialized.fontSizeClass ?? null,
    );
    node.setFormat(serialized.format);
    node.setIndent(serialized.indent);
    node.setDirection(serialized.direction);
    return node;
  }

  static importDOM(): DOMConversionMap | null {
    return HeadingNode.importDOM?.() ?? null;
  }
}

export function $isCustomHeadingNode(
  node: unknown,
): node is CustomHeadingNode {
  return node instanceof CustomHeadingNode;
}

export function $createCustomHeadingNode(
  tag: HeadingTagType,
  fontSizeClass?: HeadingFontSizeClass,
): CustomHeadingNode {
  return new CustomHeadingNode(tag, fontSizeClass);
}

export function $isHeadingClassFeatureNode(
  node: LexicalNode | null | undefined,
): node is CustomHeadingNode {
  return $isCustomHeadingNode(node);
}
